/*
  Shintop facets adapter — engineering audit 2026-10-14, R04-2 (remediation chunk 4).

  The Shintop facet layer changed Dawn's facet UI model — sort lives OUTSIDE the phone drawer, the
  drawer is an open accordion instead of a two-level menu, and the drawer set shows up to 1199px —
  but it kept the stock `facets.js` and `MenuDrawer` (global.js) that encode the old model. This file
  adapts the two from the outside. Neither Dawn file is edited: global.js never is (rule 6), and
  facets.js stays stock so a Dawn upgrade replaces it whole.

  1. SORT ON A PHONE (S03-001, S10-003). `#ShintopSortByMobile` is associated with the drawer's form
     (`form="FacetFiltersFormMobile"`, facets.liquid § mobile-sort-always), so a drawer tick — which
     facets.js serialises from that form alone — carries the sort. A change of the select itself is
     submitted by facets.js's non-drawer branch, which serialises the hidden desktop `#SortBy`; the
     mirror below writes the new value there first (facets.js waits 800ms before it reads). The
     mirror is delegated because Dawn re-renders `.sorting`, and with it `#SortBy`, after every swap.
     After every swap, Back and Forward included, the phone select is redrawn from `#SortBy`, which
     Dawn has just redrawn from the server's HTML — the one value both controls can agree on.
     The drawer footer's "Clear all" is never re-rendered, so on search its `sort_by` is the first
     render's; it is rewritten in the same pass.

  2. THE DRAWER'S SECTIONS (S06-002, S03-003, S03-015). `MenuDrawer` binds every <summary> in the
     drawer and treats each section heading as a slide-in submenu: 100ms later it stamps
     `aria-expanded="true"` whether the section opened or closed, and it calls `trapFocus()` on the
     section's panel — which REPLACES the drawer's trap with one whose first and last stops are
     hidden, so Tab walks out into the grid behind the drawer and Escape stops closing it. A
     capture-phase click listener stops that handler for section headings only (never the drawer's
     own opener); the native <details> toggle is untouched, and a `toggle` listener keeps
     `aria-expanded` true to the section. Escape closes the drawer from anywhere inside it and
     returns focus to Filter. Closing the drawer also strips `open` from every section (Dawn's
     `closeMenuDrawer`), so the drawer reopened fully collapsed: each section's state is remembered
     as the drawer closes and restored as it opens — the server opens them all, and a section the
     shopper collapsed stays collapsed, as a native <details> would.

  Every listener sits on an element facets.js never replaces (`menu-drawer`, `document`), so binding
  once is idempotent: MenuDrawer's `bindEvents()` re-runs after every render; this file never does.
*/
(() => {
  const mobileSort = () => document.getElementById('ShintopSortByMobile');
  const desktopSort = () => document.getElementById('SortBy');

  document.addEventListener('input', (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || select.name !== 'sort_by') return;
    let other = null;
    if (select.id === 'ShintopSortByMobile') other = desktopSort();
    else if (select.id === 'SortBy') other = mobileSort();
    if (other && other.value !== select.value) other.value = select.value;
  });

  const drawer = document.querySelector('menu-drawer.shintop-facets--drawer');
  const main = drawer && drawer.querySelector('details');
  const opener = main && main.querySelector(':scope > summary');
  const sections = () => (drawer ? drawer.querySelectorAll('details.mobile-facets__details') : []);

  const stamp = (details) => {
    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  const afterRender = () => {
    const desktop = desktopSort();
    const mobile = mobileSort();
    if (desktop && mobile && mobile.value !== desktop.value) mobile.value = desktop.value;

    const clearAll = document.querySelector('#FacetsWrapperMobile > .mobile-facets__footer .mobile-facets__clear');
    if (clearAll && desktop) {
      const url = new URL(clearAll.getAttribute('href'), window.location.href);
      if (url.searchParams.has('sort_by') && url.searchParams.get('sort_by') !== desktop.value) {
        url.searchParams.set('sort_by', desktop.value);
        clearAll.setAttribute('href', url.pathname + url.search);
      }
    }

    sections().forEach(stamp);
  };

  const grid = document.getElementById('ProductGridContainer');
  if (grid) new MutationObserver(afterRender).observe(grid, { childList: true });

  if (!main || !opener) return;

  const remember = () => sections().forEach((details) => (details.dataset.shintopOpen = details.open ? '1' : '0'));
  const restore = () =>
    sections().forEach((details) => {
      if (details.dataset.shintopOpen !== '0') details.open = true;
    });

  drawer.addEventListener(
    'click',
    (event) => {
      const summary = event.target.closest('summary');
      if (!summary || !drawer.contains(summary)) return;
      if (summary.classList.contains('mobile-facets__summary')) {
        event.stopPropagation();
        return;
      }
      if (summary === opener) main.open ? remember() : restore();
    },
    true
  );

  drawer.addEventListener(
    'toggle',
    (event) => {
      if (event.target.matches && event.target.matches('details.mobile-facets__details')) stamp(event.target);
    },
    true
  );

  drawer.addEventListener(
    'keyup',
    (event) => {
      if (event.key !== 'Escape' && event.code !== 'Escape') return;
      if (!main.open || typeof drawer.closeMenuDrawer !== 'function') return;
      event.stopPropagation();
      remember();
      drawer.closeMenuDrawer(event, opener);
    },
    true
  );
})();
